import API from '../../../store/helpers/api';
import { NIMBLY_API_USERS_RESET_PASSWORD } from '../../../constants/api';

/**
 * The mail endpoint's answer, reduced to what the auth screen needs.
 *
 * `kind` rather than a boolean, because a refusal and an unreachable server
 * call for opposite things: one is the person's own address being wrong and
 * must be shown, the other is ours being down and must not be reported as
 * "email not registered".
 */
export type AuthEmailRequest =
  | { kind: 'sent' }
  | { kind: 'refused'; status?: number; message?: string }
  | { kind: 'unavailable' };

/**
 * Ask api-users to send a password link.
 *
 * Deliberately has no Firebase fallback, unlike `loginWithCredentialStore`.
 * Sending the mail through `firebase.auth().sendPasswordResetEmail` is what
 * this replaces: that link lands on Firebase's own action page, which writes
 * the new password into Firebase and nowhere else, leaving the credential
 * store holding the old one. api-users owns both halves - it mints the link
 * and `/users/confirm-password` redeems it - so the password it sets is
 * mirrored. A fallback here would silently reopen exactly that divergence, on
 * the day nobody is watching.
 *
 * api-users still falls back to a Firebase-issued link itself for an
 * organization without the `auth-action-token-issuance` flag, so an unflagged
 * user receives the same email they receive today.
 *
 * Never throws: a login screen that breaks when our own API is unreachable is
 * worse than one that says "try again".
 */
async function requestAuthEmail(url: string, email: string): Promise<AuthEmailRequest> {
  try {
    // Unauthenticated: both endpoints are gateway-whitelisted precisely
    // because they run before there is a session to authenticate with.
    const api = new API();
    await api.postJSON(url, { email });
    return { kind: 'sent' };
  } catch (error: any) {
    // A 4xx is the server answering - unknown address, rate limited, already
    // disabled. Anything else, including a transport failure with no status
    // at all, is us being unable to ask.
    const status = error?.status ?? error?.response?.status;
    if (typeof status === 'number' && status >= 400 && status < 500) {
      // The status comes back with it because the caller has a localized
      // sentence for the unknown-address case and the server's own message is
      // English. Without it the only way to tell the cases apart would be to
      // match on that English text, which breaks the moment it is reworded.
      return { kind: 'refused', status, message: error?.message };
    }
    return { kind: 'unavailable' };
  }
}

/** Send the "reset your password" link for someone who already has an account. */
export const requestPasswordReset = (email: string): Promise<AuthEmailRequest> =>
  requestAuthEmail(NIMBLY_API_USERS_RESET_PASSWORD, email);

/**
 * Send the link that lets a `fresh` account set its first password. Redeeming
 * it is what clears `fresh` server-side, so it is the only way an account
 * created without a password becomes usable.
 *
 * `reset-password` rather than `activate-user`, even though the screen that
 * calls this says "Activate Account". `activate-user` answers 409 for an
 * account that is already active, and this screen is reached by an anonymous
 * visitor who cannot be asked which they are - so the narrower endpoint would
 * refuse every established user who clicked the wrong link, where the Firebase
 * call this replaced simply mailed them one. `confirmResetPassword` activates
 * a fresh profile whichever purpose the token carries, so the broader endpoint
 * serves both callers and refuses nobody it should serve. audit-admin's resend
 * button reached the same conclusion for the same reason.
 */
export const requestAccountActivation = (email: string): Promise<AuthEmailRequest> =>
  requestAuthEmail(NIMBLY_API_USERS_RESET_PASSWORD, email);
